import React from 'react'
import { Box, Button, CircularProgress } from '@mui/material'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import { TONE_BUTTON_COLOR } from './modalVariants'
import type { DialogTone } from './modalVariants'

/**
 * The single footer-button template for every dialog. Render this as the
 * `footer` node of `BaseDialog` (or let `ConfirmDialog` wire it for you) so no
 * call site hand-rolls a `<Button>` again.
 *
 * Rules this component owns (the whole point of centralising it):
 *   - Cancel is always a text button (MUI default variant).
 *   - Primary is `variant="contained"` coloured via the shared `TONE_BUTTON_COLOR`
 *     map - `neutral` -> teal (theme `primary`), `warning` -> amber, `danger` -> red.
 *     No custom `sx`; teal comes from the theme palette.
 *   - The ONLY icons allowed are a `CircularProgress` spinner while `pending`,
 *     and `OpenInNewIcon` on an external link. No decorative button icons.
 *
 * Three layout shapes fall out of which props are passed:
 *   (a) cancel + primary        - right-aligned (the default)
 *   (b) link only               - a lone link action
 *   (c) link + cancel/primary   - link far-left, the rest right (space-between)
 */
export interface DialogPrimaryAction {
  label: React.ReactNode
  onClick: () => void
  /** Intent -> colour via `TONE_BUTTON_COLOR`. Defaults to `neutral` (teal). */
  tone?: DialogTone
  /** Shows a spinner + disables the button. */
  pending?: boolean
  disabled?: boolean
}

export interface DialogCancelAction {
  label?: React.ReactNode
  onClick: () => void
  disabled?: boolean
}

export interface DialogLinkAction {
  label: React.ReactNode
  href: string
  /** External links open in a new tab and show the `OpenInNewIcon`. Default true. */
  external?: boolean
}

export interface DialogFooterProps {
  primary?: DialogPrimaryAction
  cancel?: DialogCancelAction
  link?: DialogLinkAction
}

const PrimaryButton: React.FC<{ action: DialogPrimaryAction }> = ({ action }) => (
  <Button
    variant="contained"
    color={TONE_BUTTON_COLOR[action.tone ?? 'neutral']}
    onClick={action.onClick}
    disabled={action.pending || action.disabled}
    startIcon={
      action.pending ? <CircularProgress size={16} color="inherit" /> : undefined
    }
  >
    {action.label}
  </Button>
)

const CancelButton: React.FC<{ action: DialogCancelAction }> = ({ action }) => (
  <Button onClick={action.onClick} disabled={action.disabled}>
    {action.label ?? 'Cancel'}
  </Button>
)

const LinkButton: React.FC<{ action: DialogLinkAction }> = ({ action }) => {
  const external = action.external ?? true
  return (
    <Button
      component="a"
      href={action.href}
      size="small"
      {...(external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
      endIcon={external ? <OpenInNewIcon fontSize="small" /> : undefined}
    >
      {action.label}
    </Button>
  )
}

const DialogFooter: React.FC<DialogFooterProps> = ({ primary, cancel, link }) => {
  const rightGroup = (
    <>
      {cancel && <CancelButton action={cancel} />}
      {primary && <PrimaryButton action={primary} />}
    </>
  )

  // Shape (b): a lone link - BaseDialog's DialogActions right-aligns it.
  if (link && !cancel && !primary) {
    return <LinkButton action={link} />
  }

  // Shape (c): link far-left, cancel/primary right.
  if (link) {
    return (
      <Box
        sx={{
          display: 'flex',
          width: '100%',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
        }}
      >
        <LinkButton action={link} />
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>{rightGroup}</Box>
      </Box>
    )
  }

  // Shape (a): cancel + primary, right-aligned by DialogActions.
  return rightGroup
}

export default DialogFooter
